/**
 * Tom's Personal Space — App Entry Point
 *
 * Conditionally registers rooms based on app mode (personal vs dating),
 * then boots the router.
 */

import { registerRoom, initRouter } from './router.js';
import { initNightSky } from './shared/night-sky.js';
import { getAppMode } from './lib/app-mode.js';
import { getIdentityProfile } from './lib/identity/index.js';

async function boot(): Promise<void> {
  // Deep link: ?meme=N → store target, strip param, route to dating
  const params = new URLSearchParams(window.location.search);
  const memeTarget = params.get('meme');
  if (memeTarget) {
    sessionStorage.setItem('meme_deep_link', memeTarget);
    const cleanUrl = window.location.pathname + window.location.hash;
    window.history.replaceState(null, '', cleanUrl);
    if (!window.location.hash.includes('dating')) {
      window.location.hash = '#/dating';
    }
  }

  const mode = getAppMode();

  // Prefetch identity profile (non-blocking — zones will use cached data)
  getIdentityProfile().catch(() => {
    console.warn('[boot] Identity profile fetch failed — using fallback content');
  });

  if (mode === 'dating') {
    // Dating mode: register landing first (becomes default), then dating + review
    const { landingRoom } = await import('./rooms/dating/landing.js');
    const { datingRoom } = await import('./rooms/dating/index.js');
    const { reviewRoom } = await import('./rooms/review/index.js');

    registerRoom(landingRoom);
    registerRoom(datingRoom);
    registerRoom(reviewRoom);
  } else {
    // Personal mode: register all rooms, lobby first (becomes default)
    const { lobbyRoom } = await import('./rooms/lobby/index.js');
    const { datingRoom } = await import('./rooms/dating/index.js');
    const { companionRoom } = await import('./rooms/companion/index.js');
    const { workshopRoom } = await import('./rooms/workshop/index.js');
    const { contactRoom } = await import('./rooms/contact/index.js');
    const { feedRoom } = await import('./rooms/feed/index.js');
    const { loungeRoom } = await import('./rooms/lounge/index.js');
    const { libraryRoom } = await import('./rooms/library/index.js');
    const { swlRoom } = await import('./rooms/swl/index.js');
    const { bingoRoom } = await import('./rooms/bingo/index.js');
    const { reviewRoom } = await import('./rooms/review/index.js');
    const { deskRoom } = await import('./rooms/desk/index.js');

    registerRoom(lobbyRoom);
    registerRoom(datingRoom);
    registerRoom(companionRoom);
    registerRoom(workshopRoom);
    registerRoom(contactRoom);
    registerRoom(feedRoom);
    registerRoom(loungeRoom);
    registerRoom(libraryRoom);
    registerRoom(swlRoom);
    registerRoom(bingoRoom);
    registerRoom(reviewRoom);
    registerRoom(deskRoom); // Tom OS — private, not on lobby doors
  }

  // Global night sky — real stars behind every room
  initNightSky(document.body);

  // Start router (will mount the appropriate room)
  initRouter();
}

boot().catch(console.error);
